import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { MessageList } from './streaming/MessageList';
import { ChatHeader } from './streaming/ChatHeader';
import { InputArea } from './streaming/InputArea';
import { TypingIndicator } from './TypingIndicator';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { ChatErrorBoundary } from './ChatErrorBoundary';
import { useUnifiedChat } from '@/hooks/useUnifiedChat';
import { Send, Maximize2, Minimize2, X, ChevronDown } from "lucide-react";
import { v4 as uuidv4 } from 'uuid';
import { ChatTheme, Question, TriggerRule } from "@/types/theme";
import { createNewSessionId } from '@/utils/chatLocalStorage';
import { Button } from "@/components/ui/button";
import { getPiiData } from '@/utils/piiUtils';
import { chatLogger } from "@/utils/logger";
import { usePerformanceMonitor } from "@/utils/performance";

// Import chat widget styles
import "./ChatWidget.css";
import "@/styles/chat-rich-media.css";

export interface UnifiedChatWidgetProps {
  chatbotId?: string;
  theme?: ChatTheme;
  questions?: Question[];
  triggerRules?: TriggerRule[];
  welcomeMessage?: string;
  completionMessage?: string;
  operatorName?: string;
  avatarUrl?: string | null; // Operator avatar - shown in chat message bubbles (NOT the bot avatar from builder)
  companyName?: string | null;
  companyLogo?: string | null;
  title?: string;
  initialOpen?: boolean;
  messageDelay?: number;
  buttonOptionsDelay?: number;
  noResponseDelay?: number;
  optionsHandlerDelay?: number;
  showTimestamp?: boolean;
  validationConfig?: {
    enabled?: boolean;
    maxAttempts?: number;
    strictValidation?: boolean;
  };
  onWebhookSent?: (data: any) => void;
  onClose?: () => void;
  onTitleChange?: (newTitle: string) => void;
  isFullscreen?: boolean;
  className?: string;
  piiToken?: string; // Token for accessing PII data
}

export interface UnifiedChatWidgetHandle {
  resetChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
}

/**
 * Unified Chat Widget that combines traditional and enhanced chat experiences
 * Supports both structured flows and AI-powered conversations
 */
// Helper function to process PII variables in text
const processPiiVariables = (text: string, piiData: Record<string, any> | null): string => {
  if (typeof text !== 'string') {
    return text;
  }

  // Regex to find {{variableName}} or {{variableName:defaultValue}}
  return text.replace(/\{\{\s*(\w+)(?:\s*:\s*([^}]+))?\s*\}\}/g, (match, variableName, defaultValue) => {
    // Return the value from piiData if it exists and piiData is available
    if (piiData && piiData.hasOwnProperty(variableName)) {
      return String(piiData[variableName]);
    } else if (defaultValue) {
      // Return default value if provided (even if no piiData)
      return defaultValue.trim();
    }
    // If no piiData and no default value, return the original match
    return match;
  });
};

// Process questions to replace PII variables
const processPiiInQuestions = (questions: Question[], piiData: Record<string, any> | null): Question[] => {
  return questions.map(question => ({
    ...question,
    text: processPiiVariables(question.text, piiData)
  }));
};

export const UnifiedChatWidget = forwardRef<UnifiedChatWidgetHandle, UnifiedChatWidgetProps>(({
  chatbotId,
  theme,
  questions = [],
  triggerRules = [],
  welcomeMessage = "Welcome! How can I help you today?",
  completionMessage = "Thank you for chatting with us!",
  operatorName = "Assistant",
  avatarUrl,
  companyName,
  companyLogo,
  title = "Chat With Us",
  initialOpen = false,
  messageDelay = 1000,
  buttonOptionsDelay = 0,
  noResponseDelay = 2000,
  optionsHandlerDelay = 10,
  showTimestamp = true,
  validationConfig = { enabled: false, maxAttempts: 2, strictValidation: false },
  onWebhookSent,
  onClose,
  onTitleChange,
  isFullscreen: initialFullscreen = false,
  className = "",
  piiToken,
}, ref) => {
  // State for the chat UI
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);
  const [userMessage, setUserMessage] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // PII data state
  const [piiData, setPiiData] = useState<Record<string, any> | null>(null);
  const [processedQuestions, setProcessedQuestions] = useState<Question[]>([]);
  const [processedWelcomeMessage, setProcessedWelcomeMessage] = useState(welcomeMessage);
  const [processedCompletionMessage, setProcessedCompletionMessage] = useState(completionMessage);
  const [isPiiProcessingComplete, setIsPiiProcessingComplete] = useState(false);

  // Fetch PII data if token is provided
  useEffect(() => {
    const fetchPiiData = async () => {
      try {
        // Try to get PII token from URL if not provided as prop
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = piiToken || urlParams.get('pii_token') || urlParams.get('pii');

        chatLogger.ui('PII token check:', {
          propToken: piiToken,
          urlTokenNew: urlParams.get('pii_token'),
          urlTokenOld: urlParams.get('pii'),
          finalToken: urlToken
        });

        if (!urlToken) return;

        // Fetch PII data using utility function
        const data = await getPiiData(urlToken);
        chatLogger.ui('PII data loaded:', Object.keys(data));
        setPiiData(data);
      } catch (error) {
        chatLogger.error('Error fetching PII data:', error);
      }
    };

    fetchPiiData();
  }, [piiToken]);

  // Memoize questions array to prevent infinite re-renders
  const stableQuestions = useMemo(() => questions, [JSON.stringify(questions)]);

  // Process questions, welcome message, and completion message when PII data changes
  useEffect(() => {
    chatLogger.ui('Processing PII data for messages and questions:', {
      piiDataKeys: piiData ? Object.keys(piiData) : 'No PII data',
      questionCount: stableQuestions.length,
      welcomeMessageSample: welcomeMessage.substring(0, 30) + '...',
      completionMessageSample: completionMessage.substring(0, 30) + '...'
    });

    // Always process questions with PII data (or null for default values)
    const piiProcessedQuestions = processPiiInQuestions(stableQuestions, piiData);
    setProcessedQuestions(piiProcessedQuestions);

    // Process welcome message
    const piiProcessedWelcome = processPiiVariables(welcomeMessage, piiData);
    setProcessedWelcomeMessage(piiProcessedWelcome);

    // Process completion message
    const piiProcessedCompletion = processPiiVariables(completionMessage, piiData);
    setProcessedCompletionMessage(piiProcessedCompletion);

    // Log examples of processed content
    chatLogger.ui('PII processing complete:', {
      welcomeBefore: welcomeMessage.substring(0, 30) + '...',
      welcomeAfter: piiProcessedWelcome.substring(0, 30) + '...',
      questionSample: piiProcessedQuestions.length > 0 ?
        { before: stableQuestions[0].text.substring(0, 30) + '...',
          after: piiProcessedQuestions[0].text.substring(0, 30) + '...' } :
        'No questions'
    });
    setIsPiiProcessingComplete(true);
  }, [piiData, stableQuestions, welcomeMessage, completionMessage]);

  // Debug: Log questions array only once
  useEffect(() => {
    if (processedQuestions.length > 0) {
      chatLogger.ui('Questions loaded in UnifiedChatWidget:', processedQuestions.length);
    }
  }, [processedQuestions.length]);

  // Get unified chat capabilities with structured flow and AI
  const {
    messages,
    isTyping,
    flowCompleted,
    handleUserResponse,
    startConversation,
    resetConversation,
    getAIResponse,
    streamingMessageIds,
    currentQuestionIndex
  } = useUnifiedChat({
    questions: questions, // Use original questions for real-time PII processing
    welcomeMessage: processedWelcomeMessage,
    completionMessage: processedCompletionMessage,
    chatbotId,
    messageDelay,
    buttonOptionsDelay,
    noResponseDelay,
    optionsHandlerDelay,
    validationConfig,
    onWebhookSent,
    triggerRules,
    piiData // Add piiData for real-time PII processing
  });

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    resetChat: () => {
      if (chatbotId) {
        createNewSessionId(chatbotId);
        resetConversation();
        setUserMessage("");
      }
    },
    openChat: () => setIsOpen(true),
    closeChat: () => setIsOpen(false),
    toggleChat: () => setIsOpen(prev => !prev)
  }));

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Start conversation when widget opens and PII processing is complete
  useEffect(() => {
    // Check if we have a PII token that needs to be loaded
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = piiToken || urlParams.get('pii_token') || urlParams.get('pii');

    // If we have a PII token, wait for PII data to load
    const shouldWaitForPiiData = urlToken && !piiData;

    if (isOpen && messages.length === 0 && isPiiProcessingComplete && !shouldWaitForPiiData) {
      startConversation();
    }
  }, [isOpen, messages.length, startConversation, isPiiProcessingComplete, piiData, piiToken]);

  // Handle sending user messages
  const handleSendMessage = () => {
    console.log('🔥 UnifiedChatWidget.handleSendMessage called', {
      userMessage: userMessage.substring(0, 50) + '...',
      userMessageLength: userMessage.length,
      isTyping,
      flowCompleted,
      willCallHandleUserResponse: !flowCompleted,
      willCallGetAIResponse: flowCompleted
    });
  
    if (!userMessage.trim() || isTyping) {
      console.log('🔥 UnifiedChatWidget.handleSendMessage early return', {
        emptyMessage: !userMessage.trim(),
        isTyping
      });
      return;
    }

    // For question flow or free-form chat
    if (!flowCompleted) {
      console.log('🔥 UnifiedChatWidget.handleSendMessage calling handleUserResponse');
      // Submit to question flow
      handleUserResponse(userMessage);
    } else {
      console.log('🔥 UnifiedChatWidget.handleSendMessage calling getAIResponse');
      // AI chat response after flow is completed
      getAIResponse(userMessage);
    }

    setUserMessage("");
    scrollToBottom();
  };

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);

    // Prevent scrolling when fullscreen on mobile
    if (isMobile) {
      if (!isFullscreen) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    }
  };

  // Handle closing the widget
  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setUnreadCount(0);
      setShowScrollButton(false);
    }
  };

  // Check scroll position to show/hide scroll button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Show button when scrolled up more than 200px
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 200);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Performance monitoring
  const performanceMonitor = usePerformanceMonitor();

  // Cleanup effect - prevent memory leaks
  useEffect(() => {
    return () => {
      // Remove body class if component unmounts while fullscreen
      if (document.body.classList.contains('overflow-hidden')) {
        document.body.classList.remove('overflow-hidden');
      }

      // Clear any pending timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Add ref for scroll timeout tracking
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // If widget is not open, show the chat button
  if (!isOpen) {
    return (
      <div className="chat-button-container">
        <Button
          className="chat-button"
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
          style={{
            backgroundColor: theme?.primarycolor || '#497CFD',
            color: theme?.buttontextcolor || '#FFFFFF',
            borderRadius: theme?.borderradius ? `${theme.borderradius}px` : '0.5rem',
          }}
        >
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount}</span>
          )}
          <span>Chat with us</span>
        </Button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ChatErrorBoundary>
        <div
          ref={containerRef}
          className={`unified-chat-widget ${className} ${isFullscreen ? 'fullscreen' : ''}`}
          style={{
            width: isFullscreen ? '100%' : isMobile ? '100%' : '380px',
            height: isFullscreen ? '100%' : isMobile ? '100%' : '600px',
            position: isFullscreen ? 'fixed' : 'fixed',
            bottom: isFullscreen ? 0 : isMobile ? 0 : '20px',
            right: isFullscreen ? 0 : isMobile ? 0 : '20px',
            borderRadius: isFullscreen || isMobile ? 0 : theme?.borderradius ? `${theme.borderradius}px` : '0.75rem',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme?.backgroundcolor || '#FFFFFF',
            boxShadow: isFullscreen || isMobile ? 'none' : '0 4px 24px rgba(0, 0, 0, 0.12)',
            zIndex: 1000,
            transition: 'all 0.3s ease'
          }}
          role="dialog"
          aria-labelledby="chat-header-title"
        >
          {/* Chat Header */}
          <ChatHeader
            theme={theme}
            title={title}
            isFullscreen={isFullscreen}
            onClose={handleClose}
            onToggleFullscreen={toggleFullscreen}
            onTitleChange={onTitleChange}
            companyName={companyName}
            companyLogo={companyLogo}
          />

          {/* Message List */}
          {/* Debug current question before passing to MessageList */}
          {(() => {
            const currentQuestion = currentQuestionIndex < questions.length ? questions[currentQuestionIndex] : null;
            return (
              <MessageList
                messages={messages}
                streamingMessageIds={streamingMessageIds}
                isTyping={isTyping}
                theme={theme}
                operatorName={operatorName}
                avatarUrl={avatarUrl}
                typingSpeed={400}
                autoScroll={true}
                className="flex-1"
                currentQuestion={currentQuestion}
                onOptionSelect={handleUserResponse}
                showTimestamp={showTimestamp}
              />
            );
          })()}

          {/* Scroll to bottom button */}
          {showScrollButton && (
            <button
              className="scroll-bottom-button"
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
              style={{
                position: 'absolute',
                bottom: '80px',
                right: '20px',
                backgroundColor: theme?.primarycolor || '#497CFD',
                color: '#FFFFFF',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                border: 'none',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <ChevronDown size={20} />
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount}</span>
              )}
            </button>
          )}

          {/* Input Area */}
          <InputArea
            message={userMessage}
            setMessage={setUserMessage}
            handleSendMessage={handleSendMessage}
            isDisabled={isTyping}
            theme={theme}
            placeholder={flowCompleted ? "Type your message..." : "Type your response..."}
            autoFocus={!isMobile}
          />

          {/* Invisible div for scrolling reference */}
          <div ref={messagesEndRef} />
        </div>
      </ChatErrorBoundary>
    </ErrorBoundary>
  );
});

UnifiedChatWidget.displayName = "UnifiedChatWidget";

export default UnifiedChatWidget;
